import { useEffect, useState, type ComponentType, type ReactNode } from "react";
import type { LucideProps } from "lucide-react";
import {
    autoUpdate,
    flip,
    FloatingPortal,
    offset,
    shift,
    size,
    useFloating,
} from "@floating-ui/react";

export interface ToolRailPopoverItem {
    id: string;
    label: string;
    Icon: ComponentType<LucideProps>;
    /** Popover body. Lazily rendered only while this item is open. */
    render: () => ReactNode;
}

interface ToolRailPopoverProps {
    /** Accessible label for the rail (e.g. "Diagram settings"). */
    ariaLabel: string;
    items: ReadonlyArray<ToolRailPopoverItem>;
    /**
     * Which side of the rail the popover opens toward. The rail itself is
     * expected to sit flush against the canvas on that same side.
     * @default "left"
     */
    side?: "left" | "right";
    /**
     * External "open this item" request: the item id plus a nonce that changes
     * on each request so re-requesting the same item re-opens it. Lets another
     * part of the UI jump straight to a rail section (e.g. a deep-link button).
     */
    openRequest?: { id: string; nonce: number } | null;
}

/**
 * Vertical icon rail where each button toggles a popover panel holding that
 * item's content. Mirrors {@link ToolRail} visually but, instead of selecting a
 * mode, it surfaces a settings section on demand — used by the 2D diagram to
 * keep its config "libraries" off the control panel and against the canvas.
 *
 * Self-contained: owns which popover is open, closes it on Escape or via the
 * panel's × button (not on outside-click, so editing the canvas leaves it open).
 * Presentational beyond that — each item supplies its own body.
 *
 * Positioning is delegated to Floating UI: the panel is anchored to the active
 * rail button and flips/shifts/clamps so it never leaves the viewport.
 */
export function ToolRailPopover({
    ariaLabel,
    items,
    side = "left",
    openRequest,
}: ToolRailPopoverProps) {
    const [openId, setOpenId] = useState<string | null>(null);

    // Honor an external request to open a specific item (keyed on its nonce so
    // repeats re-fire). Only opens ids the rail actually has.
    useEffect(() => {
        if (!openRequest) return;
        if (items.some((i) => i.id === openRequest.id)) {
            setOpenId(openRequest.id);
        }
    }, [openRequest, items]);

    const { refs, floatingStyles } = useFloating({
        open: openId !== null,
        placement: side === "left" ? "right-start" : "left-start",
        whileElementsMounted: autoUpdate,
        middleware: [
            offset(6),
            flip({ fallbackAxisSideDirection: "start" }),
            shift({ padding: 8 }),
            size({
                padding: 8,
                apply({ availableHeight, elements }) {
                    // Keep the panel inside the viewport vertically; its body
                    // scrolls when content is taller than the space available.
                    elements.floating.style.maxHeight = `${availableHeight}px`;
                },
            }),
        ],
    });

    // Intentionally does not close on outside-click: these panels hold config
    // libraries the user edits while interacting with the canvas, so an outside
    // click should not dismiss them. Escape and the × button remain the ways out.
    useEffect(() => {
        if (openId === null) return;
        const onKey = (e: KeyboardEvent) => {
            if (e.key === "Escape") setOpenId(null);
        };
        window.addEventListener("keydown", onKey);
        return () => {
            window.removeEventListener("keydown", onKey);
        };
    }, [openId]);

    const openItem = items.find((i) => i.id === openId) ?? null;

    return (
        <div className={`tool-rail-popover tool-rail-popover--${side}`}>
            <div
                className="tool-rail tool-rail--popover"
                role="toolbar"
                aria-label={ariaLabel}
                aria-orientation="vertical"
            >
                {items.map(({ id, label, Icon }) => {
                    const active = id === openId;
                    return (
                        <button
                            key={id}
                            ref={active ? refs.setReference : undefined}
                            type="button"
                            className={`tool-rail-btn${active ? " active" : ""}`}
                            onClick={() =>
                                setOpenId((cur) => (cur === id ? null : id))
                            }
                            title={label}
                            aria-label={label}
                            aria-haspopup="dialog"
                            aria-expanded={active}
                        >
                            <Icon className="tool-rail-icon" size={18} aria-hidden />
                        </button>
                    );
                })}
            </div>
            {openItem && (
                <FloatingPortal>
                    <div
                        ref={refs.setFloating}
                        className="tool-rail-popover-panel"
                        style={floatingStyles}
                        role="dialog"
                        aria-label={openItem.label}
                    >
                        <div className="tool-rail-popover-panel__header">
                            <span className="tool-rail-popover-panel__title">
                                {openItem.label}
                            </span>
                            <button
                                type="button"
                                className="tool-rail-popover-panel__close"
                                onClick={() => setOpenId(null)}
                                aria-label="Close"
                            >
                                ×
                            </button>
                        </div>
                        <div className="tool-rail-popover-panel__body">
                            {openItem.render()}
                        </div>
                    </div>
                </FloatingPortal>
            )}
        </div>
    );
}
